/** @jsx jsx */
import * as React from "react";
import { css, jsx } from "@emotion/core";
import { IFetchScheduledInterventionsProgresssResponse } from "../../models/IFetchScheduledInterventions";
import { DonutChart } from "../common/DonutChart";
import { interventionsSummaryStyles } from "../../styles/Interventions/interventionsSummary";
import { CustomDateRangePicker } from "../common/CustomDateRangePicker";
import * as moment from "moment"
import { Card } from "../common/Card";
import { ProgressBar } from "../common/ProgressBar";
import { DateRange } from 'moment-range';

interface IIntervantionsSummaryProps {
    data: IFetchScheduledInterventionsProgresssResponse[];
    startDate: number;
    endDate: number;
    dateRangeChangeHandler(startDate: number, endDate: number): void;
    isMobile: boolean;
}

enum TIME_OPTIONS {
    WEEK = "WEEK",
    ALL_TIME = "ALL_TIME",
    CUSTOM_DATE = "CUSTOM_DATE"
}

interface IIntervantionsSummaryState {
    selectedTimeOption: TIME_OPTIONS;
    isDateRangePickerVisible: boolean;
}


export class InterventionsSummary extends React.Component<IIntervantionsSummaryProps, IIntervantionsSummaryState> {
    constructor(props: IIntervantionsSummaryProps) {
        super(props);
        this.state = {
            selectedTimeOption: TIME_OPTIONS.WEEK,
            isDateRangePickerVisible: false
        };
        this.getStats = this.getStats.bind(this);
        this.timeRangeSchemeChange = this.timeRangeSchemeChange.bind(this);
        this.selectDateRange = this.selectDateRange.bind(this);
        this.getDesktopDesign = this.getDesktopDesign.bind(this);
        this.getMobileDesign = this.getMobileDesign.bind(this);
        this.hideRangePicker = this.hideRangePicker.bind(this);
    }
    private getStats(): { total: number, complete: number, incomplete: number, noResposne: number } {
        const stats = { total: 0, complete: 0, incomplete: 0, noResposne: 0 };
        if (this.props.data && this.props.data.length) {
            this.props.data.forEach(stat => {
                stats.total = stats.total + stat.totalInterventions;
                stats.complete = stats.complete + stat.completedInterventions;
                stats.incomplete = stats.incomplete + (stat.totalInterventions - stat.completedInterventions);
                stats.noResposne = stats.total - (stats.complete + stats.incomplete);
            })
        }
        return stats;
    }

    private timeRangeSchemeChange(selectedTimeOption: TIME_OPTIONS) {
        this.setState({ selectedTimeOption });
        if (selectedTimeOption === TIME_OPTIONS.CUSTOM_DATE) {
            this.setState({ isDateRangePickerVisible: true });
        } else if (selectedTimeOption === TIME_OPTIONS.WEEK) {
            this.setState({ isDateRangePickerVisible: false });
            const endDate = new Date().setHours(0, 0, 0, 0);
            const startDate = endDate - (7 * 24 * 60 * 60 * 1000);
            this.props.dateRangeChangeHandler(startDate, endDate);
        } else {
            this.setState({ isDateRangePickerVisible: false });
        }
    }

    private getDonutChartColorScheme(): string[] {
        const colorSchemes: string[][] = [
            ["#7B5466", "#9A6989", "#B77FB0", "#CF95D5", "#DDACF2", "#D6B6F5", "#D2C0F7", "#D2CBF9", "#D6D8FB", "#E1E8FD", "#EDF4FE"],
            ["#59547B", "#696D9A", "#7F8FB7", "#95B5D5", "#ACDEF2", "#B6EFF5", "#C0F7F2", "#CBF9ED", "#D6FBEA", "#E1FDEB", "#EDFEF0"],
            ["#79547B", "#8C6A9A", "#9C80B7", "#A996D5", "#B3ADF2", "#B7BDF5", "#C1D1F7", "#CCE1F9", "#D7EFFB", "#E2F8FD", "#EDFEFD"],
            ["#506E64", "#658975", "#7AA383", "#8FBE90", "#AED8A4", "#C1DEAF", "#D2E4BB", "#E1E9C7", "#EDEED3", "#F3F1DF", "#F8F4EC"]
        ]
        const randomNumber: number = Math.floor(Math.random() * 5);
        return colorSchemes[randomNumber];
    }

    private hideRangePicker() {
        this.setState({ isDateRangePickerVisible: false });
    }

    private selectDateRange(dates: DateRange) {
        this.setState({ isDateRangePickerVisible: false }, () => {
            if (dates) {
                const startDate: number = dates.start.unix() * 1000;
                const endDate: number = dates.end.unix() * 1000;
                this.props.dateRangeChangeHandler(startDate, endDate);
            }
        });
    }

    getDesktopDesign(data: IFetchScheduledInterventionsProgresssResponse[]) {
        return (<div css={interventionsSummaryStyles.chartsContainer}>
            {data && data.map((val, mainIdx) => {
                return (
                    <React.Fragment key={mainIdx}>
                        {
                            val.categoryProgressList.map((catData, index) => {
                                const chartData = { complete: catData.completedInterventions, incomplete: catData.totalInterventions };
                                const percentComplete: number = (catData.completedInterventions / catData.totalInterventions) * 100;
                                return (<div key={index} css={interventionsSummaryStyles.chartWithLegend}>
                                    <DonutChart width={80} height={80} innnerRadius={35} margin={10} data={chartData} containerClassName={`interventions-donut-chart-${index}`} index={index} />
                                    <div css={interventionsSummaryStyles.percentText}>{Math.ceil(percentComplete)}%</div>
                                    <div css={interventionsSummaryStyles.legendContainer}>
                                        <div>{catData.interventionCategory || "N.A"}</div>
                                        <div css={{ fontSize: 15 }}>{`${catData.completedInterventions}/${catData.totalInterventions}`} Complete</div>
                                    </div>
                                </div>
                                )
                            })
                        }
                    </React.Fragment>

                )
            })}
        </div>)
    }

    getMobileDesign(data: IFetchScheduledInterventionsProgresssResponse[]) {
        return (<div css={interventionsSummaryStyles.summaryProgressContainer}>
            {data && data.map((val, mainIdx) => {
                return (
                    <React.Fragment key={mainIdx}>
                        {
                            val.categoryProgressList.map((catData, index) => {
                                const chartData = { complete: catData.completedInterventions, incomplete: catData.totalInterventions };
                                const percentComplete: number = (catData.completedInterventions / catData.totalInterventions) * 100;
                                const categoryColor: any = this.getCategoryColor(catData.interventionCategory);
                                return (
                                    <React.Fragment key={index}>
                                        <div css={interventionsSummaryStyles.categoryContainer}>
                                            <div css={interventionsSummaryStyles.categoryName}>{catData.interventionCategory || "N.A"}</div>
                                            <div css={interventionsSummaryStyles.categoryProgress}>{`${catData.completedInterventions}/${catData.totalInterventions}`} Complete</div>
                                        </div>
                                        <div css={interventionsSummaryStyles.progressBarWrapper}>
                                            <div css={interventionsSummaryStyles.percentComplete}>{Math.ceil(percentComplete)}%</div>
                                            <ProgressBar data={percentComplete} containerClassName={`interventions-progress-chart-${index}`} trailColor={categoryColor.incomplete} completeColor={categoryColor.complete} />
                                        </div>
                                    </React.Fragment>
                                )
                            })
                        }
                    </React.Fragment>

                )
            })}
        </div>)
    }

    getCategoryColor(category: string) {
        switch (category) {
            case "MEAL": return { complete: "rgb(178, 171, 242)", incomplete: "rgb(178, 171, 242, 0.2)" }

            case "WORKOUT": return { complete: "rgb(220, 171, 242)", incomplete: "rgb(220, 171, 242, 0.2)" }

            case "MIND": return { complete: "rgb(171, 220, 242)", incomplete: "rgb(171, 220, 242, 0.2)" }

            case "SLEEP": return { complete: "rgb(173, 215, 163)", incomplete: "rgb(173, 215, 163, 0.2)" }

            case "MEDICINE": return { complete: "rgb(173, 215, 163)", incomplete: "rgb(173, 215, 163, 0.2)" }

            default: return { complete: "rgb(173, 215, 163)", incomplete: "rgb(173, 215, 163, 0.2)" }
        }
    }

    render() {
        const { total, complete, incomplete, noResposne } = this.getStats();
        const { data, startDate, endDate, isMobile } = this.props;
        const { selectedTimeOption, isDateRangePickerVisible } = this.state;
        const startMoment = new moment()

        return (<div css={interventionsSummaryStyles.summaryCardWrapper}>
            <Card customCss={interventionsSummaryStyles.wrapperCard} showShadow>
                <div css={interventionsSummaryStyles.summaryContainer}>
                    <div css={interventionsSummaryStyles.cardHeaderText}>Summary</div>
                    <div css={interventionsSummaryStyles.timeRangeContainer}>
                        <button onClick={() => this.timeRangeSchemeChange(TIME_OPTIONS.WEEK)} css={selectedTimeOption === TIME_OPTIONS.WEEK ? interventionsSummaryStyles.timeSelectorButtonSelected : interventionsSummaryStyles.timeSelectorButton}>This Week</button>
                        <button onClick={() => this.timeRangeSchemeChange(TIME_OPTIONS.ALL_TIME)} css={selectedTimeOption === TIME_OPTIONS.ALL_TIME ? interventionsSummaryStyles.timeSelectorButtonSelected : interventionsSummaryStyles.timeSelectorButton}>All Time</button>
                        <button onClick={() => this.timeRangeSchemeChange(TIME_OPTIONS.CUSTOM_DATE)} css={selectedTimeOption === TIME_OPTIONS.CUSTOM_DATE ? interventionsSummaryStyles.timeSelectorButtonSelected : interventionsSummaryStyles.timeSelectorButton}>Custom Date</button>
                    </div>
                    <div css={interventionsSummaryStyles.seperator}></div>
                    <div css={interventionsSummaryStyles.kpiContainer}>
                        <div>
                            <div css={interventionsSummaryStyles.kpiBoldText}>{total}</div>
                            <div css={interventionsSummaryStyles.kpiSmallText}>{isMobile ? "Total" : "Total Interventions"}</div>
                        </div>
                        <div>
                            <div><span css={interventionsSummaryStyles.kpiBoldText}>{complete}</span>/<span css={interventionsSummaryStyles.kpiSmallText}>{total}</span></div>
                            <div css={interventionsSummaryStyles.kpiSmallText}>Complete</div>
                        </div>
                        <div>
                            <div><span css={interventionsSummaryStyles.kpiBoldText}>{incomplete}</span>/<span css={interventionsSummaryStyles.kpiSmallText}>{total}</span></div>
                            <div css={interventionsSummaryStyles.kpiSmallText}>Incomplete</div>
                        </div>
                        <div>
                            <div><span css={interventionsSummaryStyles.kpiBoldText}>{noResposne}</span>/<span css={interventionsSummaryStyles.kpiSmallText}>{total}</span></div>
                            <div css={interventionsSummaryStyles.kpiSmallText}>No Response</div>
                        </div>
                    </div>
                    {isMobile ? this.getMobileDesign(data) : this.getDesktopDesign(data)}
                </div>
            </Card>
            {
                isDateRangePickerVisible &&
                <CustomDateRangePicker onCancel={this.hideRangePicker} onOk={this.selectDateRange} isMobile={isMobile} />
            }
        </div >)
    }
}